import { useMemo } from "react";
import { X, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const PRINCIPLE_LABELS = {
  safety: "Safety",
  trustworthiness: "Trustworthiness",
  choice: "Choice",
  collaboration: "Collaboration",
  empowerment: "Empowerment",
};

const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: -24 };
const TICK_STYLE = { fontSize: 11, fill: "#a8a29e" };
const AXIS_LINE = { stroke: "#e7e5e4" };
const Y_DOMAIN = [0, 100];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 10, border: "1px solid #e7e5e4" };
const DOT_STYLE = { r: 3, fill: "#047857" };

function tonePoints(tone) {
  if (tone === "good") return 1;
  if (tone === "caution") return 0.5;
  return 0;
}

function trendIcon(delta) {
  if (delta > 0) return TrendingUp;
  if (delta < 0) return TrendingDown;
  return Minus;
}

function deltaClass(delta) {
  if (delta > 0) return "text-emerald-700";
  if (delta < 0) return "text-rose-700";
  return "text-stone-500";
}

function barColor(pct) {
  if (pct >= 70) return "bg-emerald-500";
  if (pct >= 45) return "bg-amber-400";
  return "bg-rose-400";
}

const principleStats = (sessions) => {
  const agg = {};
  sessions.forEach((s) =>
    (s.choices || []).forEach((c) => {
      if (!c.principle) return;
      const a = (agg[c.principle] ||= { pts: 0, total: 0 });
      a.total += 1;
      a.pts += tonePoints(c.tone);
    })
  );
  return Object.entries(agg)
    .map(([key, a]) => ({ key, label: PRINCIPLE_LABELS[key] || key, pct: Math.round((a.pts / a.total) * 100), total: a.total }))
    .sort((a, b) => b.pct - a.pct);
};

const tooltipLabel = (label, payload) =>
  payload?.[0] ? `${payload[0].payload.case} · ${payload[0].payload.date}` : `Run ${label}`;

const tooltipValue = (v) => [`${v}%`, "Score"];

const ScoreTrendChart = ({ trend, avg }) => {
  if (trend.length < 2) {
    return <p className="mt-3 text-sm text-stone-400">One run so far. The trend appears after a second case.</p>;
  }
  return (
    <div className="mt-2 h-44">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trend} margin={CHART_MARGIN}>
          <XAxis dataKey="run" tick={TICK_STYLE} tickLine={false} axisLine={AXIS_LINE} />
          <YAxis domain={Y_DOMAIN} tick={TICK_STYLE} tickLine={false} axisLine={false} />
          <ReferenceLine y={avg} stroke="#d6d3d1" strokeDasharray="4 4" />
          <Tooltip formatter={tooltipValue} labelFormatter={tooltipLabel} contentStyle={TOOLTIP_STYLE} />
          <Line type="monotone" dataKey="score" stroke="#047857" strokeWidth={2} dot={DOT_STYLE} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const PrincipleBars = ({ principles }) => {
  const strongest = principles[0];
  const weakest = principles.length > 1 ? principles[principles.length - 1] : null;
  if (principles.length === 0) {
    return <p className="text-sm text-stone-400">No principle data recorded yet.</p>;
  }
  return (
    <div className="mt-3 space-y-2.5">
      {principles.map((p) => (
        <div key={p.key} data-testid={`principle-${p.key}`}>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-stone-700">
              {p.label}
              {strongest?.key === p.key && principles.length > 1 && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">Strongest</span>
              )}
              {weakest?.key === p.key && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">Focus area</span>
              )}
            </span>
            <span className="tabular-nums text-stone-500">{p.pct}%</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-stone-100 overflow-hidden">
            <div className={`h-full rounded-full ${barColor(p.pct)}`} style={{ width: `${p.pct}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const KnowledgeLine = ({ knowledge }) => {
  if (!knowledge?.pre && !knowledge?.post) return null;
  const delta = knowledge.pre && knowledge.post ? knowledge.post.score - knowledge.pre.score : null;
  return (
    <p data-testid="drilldown-knowledge" className="mt-1 text-sm text-stone-500">
      Knowledge check: {knowledge.pre ? `${knowledge.pre.score}/${knowledge.pre.total} before` : "not yet taken before"}
      {knowledge.post ? `, ${knowledge.post.score}/${knowledge.post.total} after` : ""}
      {delta != null && (
        <span className={`ml-2 font-medium ${delta > 0 ? "text-emerald-700" : "text-stone-500"}`}>
          ({delta >= 0 ? "+" : ""}{delta})
        </span>
      )}
    </p>
  );
};

export const TraineeDrilldown = ({ name, sessions, knowledge, onClose }) => {
  const runs = useMemo(
    () => [...sessions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [sessions]
  );
  const trend = runs.map((s, i) => ({
    run: i + 1,
    score: s.score,
    case: s.scenario_title,
    date: new Date(s.created_at).toLocaleDateString(undefined, { dateStyle: "medium" }),
  }));
  const principles = useMemo(() => principleStats(runs), [runs]);
  const avg = runs.length ? Math.round(runs.reduce((a, r) => a + r.score, 0) / runs.length) : 0;
  const delta = runs.length >= 2 ? runs[runs.length - 1].score - runs[0].score : 0;
  const TrendIcon = trendIcon(delta);

  return (
    <div data-testid="trainee-drilldown" className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 sm:p-7">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">Trainee detail</p>
          <h2 data-testid="drilldown-name" className="mt-1 font-heading text-2xl font-semibold text-stone-900">{name}</h2>
          <p className="mt-1 text-sm text-stone-500">
            {runs.length} {runs.length === 1 ? "run" : "runs"} · {avg}% average
            {runs.length >= 2 && (
              <span className={`ml-2 inline-flex items-center gap-1 font-medium ${deltaClass(delta)}`}>
                <TrendIcon className="h-3.5 w-3.5" />
                {delta > 0 ? "+" : ""}{delta} pts since first run
              </span>
            )}
          </p>
          <KnowledgeLine knowledge={knowledge} />
        </div>
        <button
          data-testid="drilldown-close"
          onClick={onClose}
          aria-label="Close trainee detail"
          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-stone-400 hover:bg-white hover:text-stone-700 transition-colors duration-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Score trend</p>
          <ScoreTrendChart trend={trend} avg={avg} />
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Trauma-informed principles</p>
          <PrincipleBars principles={principles} />
        </div>
      </div>
    </div>
  );
};
